import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import DesktopLayout from '../../../components/layout/DesktopLayout';
import * as adminOrdersApi from '../../../services/adminOrdersApi';
import type { AdminOrder, AdminOrderFilters } from '../../../services/adminOrdersApi';
import './AdminOrdersPage.css';

const STATUS_OPTIONS = [
  { value: '', label: '全部訂單' },
  { value: 'pending', label: '待處理' },
  { value: 'processing', label: '處理中' },
  { value: 'shipped', label: '已出貨' },
  { value: 'delivered', label: '已送達' },
  { value: 'cancelled', label: '已取消' },
  { value: 'failed', label: '失敗' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: '#ff9800',
  processing: '#2196f3',
  shipped: '#9c27b0',
  delivered: '#4caf50',
  cancelled: '#f44336',
  failed: '#f44336',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '待處理',
  processing: '處理中',
  shipped: '已出貨',
  delivered: '已送達',
  cancelled: '已取消',
  failed: '失敗',
};

export default function AdminOrdersListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Filter states
  const [filters, setFilters] = useState<AdminOrderFilters>({
    status: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    page: 0,
    limit: 20,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await adminOrdersApi.getAdminOrders(
        { ...filters, search: searchTerm || undefined },
        localStorage.getItem('auth_token') || ''
      );

      setOrders(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入訂單失敗');
    } finally {
      setIsLoading(false);
    }
  }, [filters, searchTerm]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Handle filter change
  const handleFilterChange = (key: keyof AdminOrderFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 0 }));
  };

  // Handle search
  const handleSearch = () => {
    setFilters(prev => ({ ...prev, page: 0 }));
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Format price
  const formatPrice = (price: number) => {
    return `$${Math.round(price).toLocaleString()}`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle order click
  const handleOrderClick = (orderId: string) => {
    navigate(`/admin/orders/${orderId}`);
  };

  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return (
      <DesktopLayout>
        <div className="admin-orders-page">
          <div className="admin-orders-page__error">
            您沒有權限訪問此頁面
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className="admin-orders-page">
        <header className="admin-orders-page__header">
          <h1>訂單管理</h1>
        </header>

        {/* Filters */}
        <div className="admin-orders-page__filters">
          {/* Status Filter */}
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
            className="admin-orders-page__filter-select"
          >
            {STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Date Range */}
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            className="admin-orders-page__date-input"
          />
          <span className="admin-orders-page__date-separator">至</span>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            className="admin-orders-page__date-input"
          />
          <button
            onClick={() => {
              setFilters(prev => ({ ...prev, startDate: dateRange.startDate, endDate: dateRange.endDate, page: 0 }));
            }}
            className="admin-orders-page__filter-button"
          >
            套用
          </button>

          {/* Search */}
          <input
            type="text"
            placeholder="搜尋訂單編號、客戶姓名、Email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="admin-orders-page__search-input"
          />
          <button
            onClick={handleSearch}
            className="admin-orders-page__search-button"
          >
            搜尋
          </button>

          {/* Sort */}
          <select
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('-') as ['createdAt' | 'total' | 'orderNumber', 'asc' | 'desc'];
              setFilters(prev => ({ ...prev, sortBy, sortOrder, page: 0 }));
            }}
            className="admin-orders-page__filter-select"
          >
            <option value="createdAt-desc">建立時間（新→舊）</option>
            <option value="createdAt-asc">建立時間（舊→新）</option>
            <option value="total-desc">訂單金額（高→低）</option>
            <option value="total-asc">訂單金額（低→高）</option>
            <option value="orderNumber-asc">訂單編號（A→Z）</option>
          </select>
        </div>

        {/* Error */}
        {error && (
          <div className="admin-orders-page__error">
            {error}
            <button onClick={() => setError(null)} className="admin-orders-page__error-close">×</button>
          </div>
        )}

        {/* Loading */}
        {isLoading ? (
          <div className="admin-orders-page__loading">載入中...</div>
        ) : orders.length === 0 ? (
          <div className="admin-orders-page__empty">
            <p>沒有符合條件的訂單</p>
          </div>
        ) : (
          <>
            {/* Orders Table */}
            <div className="admin-orders-page__table-container">
              <table className="admin-orders-page__table">
                <thead>
                  <tr>
                    <th>訂單編號</th>
                    <th>客戶</th>
                    <th>金額</th>
                    <th>狀態</th>
                    <th>建立時間</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order.id}
                      onClick={() => handleOrderClick(order.id)}
                      className="admin-orders-page__table-row"
                    >
                      <td className="admin-orders-page__order-number">{order.orderNumber}</td>
                      <td>
                        <div className="admin-orders-page__customer-name">{order.customer.name}</div>
                        <div className="admin-orders-page__customer-email">{order.customer.email}</div>
                      </td>
                      <td className="admin-orders-page__total">{formatPrice(order.total)}</td>
                      <td>
                        <span
                          className="admin-orders-page__status-badge"
                          style={{ backgroundColor: STATUS_COLORS[order.status] }}
                        >
                          {STATUS_LABELS[order.status] || order.status}
                        </span>
                      </td>
                      <td className="admin-orders-page__date">{formatDate(order.createdAt)}</td>
                      <td>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOrderClick(order.id);
                          }}
                          className="admin-orders-page__view-button"
                        >
                          查看
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="admin-orders-page__pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 0}
                  className="admin-orders-page__pagination-button"
                >
                  上一頁
                </button>
                <span className="admin-orders-page__pagination-info">
                  第 {pagination.page + 1} 頁，共 {pagination.totalPages} 頁（{pagination.total} 筆）
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages - 1}
                  className="admin-orders-page__pagination-button"
                >
                  下一頁
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </DesktopLayout>
  );
}
